/**
 * SoftSignal Shopify Theme Engine
 * Online Store 2.0 Native AJAX Interactions & Utilities
 */

(function () {
  'use strict';

  // Global State
  window.SoftSignal = {
    cart: null,
    moneyFormat: 'Rs. {{amount}}',
    freeShippingGoal: 1999,
  };

  // Toast Notifications
  function showToast(message, duration = 3500) {
    let container = document.querySelector('.toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'toast-container';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#df4864" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
      <span>${message}</span>
    `;
    container.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      toast.style.transition = 'all 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }
  window.showToast = showToast;

  // Format Money helper
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeSettings?.moneyFormat || 'Rs. {{amount}}');
    }
    const amount = (cents / 100).toFixed(2);
    return `Rs. ${amount}`;
  }

  // --- CART DRAWER API ---
  const CartDrawer = {
    init() {
      this.drawer = document.getElementById('cart-drawer');
      this.overlay = document.getElementById('cart-drawer-overlay');
      this.itemsContainer = document.getElementById('cart-drawer-items');
      this.subtotalEl = document.getElementById('cart-drawer-subtotal');
      this.countBadges = document.querySelectorAll('.cart-badge-count');
      this.shippingBarFill = document.getElementById('cart-shipping-fill');
      this.shippingBarText = document.getElementById('cart-shipping-text');

      if (!this.drawer) return;

      this.bindEvents();
      this.fetchCart();
    },

    bindEvents() {
      // Cart open triggers
      document.querySelectorAll('[data-cart-drawer-trigger]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Cart close triggers
      document.querySelectorAll('[data-cart-drawer-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Quantity & Remove delegate
      if (this.itemsContainer) {
        this.itemsContainer.addEventListener('click', (e) => {
          const target = e.target.closest('[data-cart-action]');
          if (!target) return;

          const key = target.dataset.lineKey;
          const currentQty = parseInt(target.dataset.qty, 10);
          const action = target.dataset.cartAction;

          if (action === 'plus') {
            this.changeItem(key, currentQty + 1);
          } else if (action === 'minus') {
            this.changeItem(key, Math.max(0, currentQty - 1));
          } else if (action === 'remove') {
            this.changeItem(key, 0);
          }
        });
      }

      // Intercept AJAX add to cart forms
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;

        e.preventDefault();
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn ? submitBtn.innerHTML : '';
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span>Adding...</span>';
        }

        const formData = new FormData(form);
        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        })
          .then((res) => res.json())
          .then((item) => {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.innerHTML = originalText;
            }
            if (item.status && item.status !== 200) {
              showToast(item.description || 'Could not add item to bag');
              return;
            }
            showToast(`Added "${item.title}" to bag!`);
            this.fetchCart().then(() => this.open());
          })
          .catch((err) => {
            console.error(err);
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.innerHTML = originalText;
            }
            form.submit(); // fallback to standard submit
          });
      });
    },

    open() {
      if (this.drawer && this.overlay) {
        this.drawer.classList.add('is-open');
        this.overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      }
    },

    close() {
      if (this.drawer && this.overlay) {
        this.drawer.classList.remove('is-open');
        this.overlay.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    },

    async fetchCart() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        window.SoftSignal.cart = cart;
        this.render(cart);
        return cart;
      } catch (err) {
        console.error('Failed to fetch cart:', err);
      }
    },

    async changeItem(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
        const cart = await res.json();
        window.SoftSignal.cart = cart;
        this.render(cart);
      } catch (err) {
        console.error('Failed to update cart item:', err);
      }
    },

    render(cart) {
      if (!cart) return;

      // Update badge counts
      this.countBadges.forEach((el) => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      // Update subtotal
      if (this.subtotalEl) {
        this.subtotalEl.textContent = formatMoney(cart.total_price);
      }

      // Update free shipping bar
      const thresholdCents = (window.themeSettings?.freeShippingThreshold || 1999) * 100;
      if (this.shippingBarFill && this.shippingBarText) {
        const remaining = thresholdCents - cart.total_price;
        const progress = Math.min(100, (cart.total_price / thresholdCents) * 100);
        this.shippingBarFill.style.width = `${progress}%`;

        if (remaining <= 0) {
          this.shippingBarText.innerHTML = window.themeSettings?.freeShippingSuccessText || '🎉 You unlocked FREE Express Shipping!';
        } else {
          const remainingFormatted = formatMoney(remaining);
          const rawText = window.themeSettings?.freeShippingProgressText || 'Add [amount] more to unlock FREE Shipping!';
          this.shippingBarText.innerHTML = rawText.replace('[amount]', `<strong>${remainingFormatted}</strong>`);
        }
      }

      // Render cart items
      if (this.itemsContainer) {
        if (cart.item_count === 0) {
          this.itemsContainer.innerHTML = `
            <div style="text-align: center; padding: 3rem 1rem;">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(15,13,13,0.3)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin: 0 auto 1rem auto;"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              <h3 style="font-size: 1.2rem; font-weight: 800; margin-bottom: 0.5rem;">Your bag is empty</h3>
              <p style="font-size: 0.88rem; color: var(--color-text-muted); margin-bottom: 1.5rem;">Pick something flirty before someone else does.</p>
              <a href="/collections/all" class="btn btn-primary btn-sm" onclick="SoftSignal.CartDrawer.close()">Explore Catalog</a>
            </div>
          `;
          return;
        }

        this.itemsContainer.innerHTML = cart.items
          .map((item) => {
            const variantTitle = item.variant_title ? `<div class="cart-item__variant">${item.variant_title}</div>` : '';
            return `
            <div class="cart-item">
              <img src="${item.featured_image.url || item.image}" alt="${item.title}" class="cart-item__img" />
              <div>
                <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
                ${variantTitle}
                <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
                <div style="display: flex; align-items: center; gap: 0.5rem; margin-top: 0.6rem;">
                  <div class="quantity-selector" style="transform: scale(0.85); transform-origin: left center;">
                    <button type="button" class="quantity-btn" data-cart-action="minus" data-line-key="${item.key}" data-qty="${item.quantity}">-</button>
                    <span class="quantity-input" style="display:inline-flex;align-items:center;justify-content:center;">${item.quantity}</span>
                    <button type="button" class="quantity-btn" data-cart-action="plus" data-line-key="${item.key}" data-qty="${item.quantity}">+</button>
                  </div>
                </div>
              </div>
              <button type="button" data-cart-action="remove" data-line-key="${item.key}" style="color: var(--color-text-subtle); padding: 0.5rem;" title="Remove">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          `;
          })
          .join('');
      }
    },
  };
  window.SoftSignal.CartDrawer = CartDrawer;

  // --- QUICK VIEW MODAL API ---
  const QuickView = {
    init() {
      this.modal = document.getElementById('quickview-modal');
      this.content = document.getElementById('quickview-content');
      if (!this.modal) return;

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quickview-handle]');
        if (!btn) return;

        e.preventDefault();
        const handle = btn.dataset.quickviewHandle;
        this.open(handle);
      });

      this.modal.querySelectorAll('[data-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
    },

    async open(handle) {
      if (!handle || !this.modal || !this.content) return;
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.content.innerHTML = `
        <div style="padding: 4rem 2rem; text-align: center;">
          <div style="display:inline-block;width:32px;height:32px;border:3px solid var(--color-primary);border-top-color:transparent;border-radius:50%;animation:spin 0.8s linear infinite;"></div>
          <p style="margin-top: 1rem; font-weight: 700;">Loading product...</p>
        </div>
      `;

      try {
        const res = await fetch(`/products/${handle}?view=quickview`);
        const html = await res.text();
        this.content.innerHTML = html;
        this.bindQuickViewForm();
      } catch (err) {
        console.error('Failed to load quick view:', err);
        this.content.innerHTML = '<p style="padding: 2rem; text-align: center;">Product preview unavailable.</p>';
      }
    },

    bindQuickViewForm() {
      const form = this.content.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      const variantSelect = form.querySelector('select[name="id"]');
      const sizeOptions = form.querySelectorAll('.variant-option');

      sizeOptions.forEach((opt) => {
        opt.addEventListener('click', () => {
          sizeOptions.forEach((o) => o.classList.remove('is-selected'));
          opt.classList.add('is-selected');
          if (variantSelect) {
            variantSelect.value = opt.dataset.variantId;
          }
        });
      });
    },

    close() {
      if (this.modal) {
        this.modal.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    },
  };
  window.SoftSignal.QuickView = QuickView;

  // --- SIZE GUIDE MODAL API ---
  const SizeGuide = {
    init() {
      this.modal = document.getElementById('size-guide-modal');
      if (!this.modal) return;

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-size-guide-trigger]')) {
          e.preventDefault();
          this.modal.classList.add('is-active');
          document.body.style.overflow = 'hidden';
        }
      });

      this.modal.querySelectorAll('[data-modal-close]').forEach((el) => {
        el.addEventListener('click', () => {
          this.modal.classList.remove('is-active');
          document.body.style.overflow = '';
        });
      });
    },
  };

  // --- PRODUCT PAGE VARIANT SWITCHER ---
  function initProductVariants() {
    const productSections = document.querySelectorAll('[data-section-type="product"]');
    productSections.forEach((section) => {
      const masterSelect = section.querySelector('select[name="id"]');
      const priceMain = section.querySelector('.product-info__price-main');
      const priceCompare = section.querySelector('.product-info__price-compare');
      const mainImg = section.querySelector('.product-gallery__main img');
      const thumbs = section.querySelectorAll('.product-gallery__thumb');
      const optionButtons = section.querySelectorAll('.variant-option');

      // Thumbnail click
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          const newSrc = thumb.dataset.imageSrc;
          if (mainImg && newSrc) {
            mainImg.src = newSrc;
          }
        });
      });

      // Variant pill click
      optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const optionIndex = btn.dataset.optionIndex;
          section.querySelectorAll(`.variant-option[data-option-index="${optionIndex}"]`).forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');

          const variantId = btn.dataset.variantId;
          const variantPrice = btn.dataset.variantPrice;
          const variantComparePrice = btn.dataset.variantComparePrice;
          const variantImage = btn.dataset.variantImage;

          if (masterSelect && variantId) {
            masterSelect.value = variantId;
          }
          if (priceMain && variantPrice) {
            priceMain.textContent = variantPrice;
          }
          if (priceCompare) {
            if (variantComparePrice) {
              priceCompare.textContent = variantComparePrice;
              priceCompare.style.display = 'inline';
            } else {
              priceCompare.style.display = 'none';
            }
          }
          if (mainImg && variantImage) {
            mainImg.src = variantImage;
          }
        });
      });
    });
  }

  // --- ACCORDION TABS ---
  function initAccordions() {
    document.addEventListener('click', (e) => {
      const header = e.target.closest('.accordion-header');
      if (!header) return;

      const item = header.closest('.accordion-item');
      const content = item.querySelector('.accordion-content');
      const isOpen = header.classList.contains('is-open');

      if (isOpen) {
        header.classList.remove('is-open');
        content.classList.remove('is-open');
      } else {
        header.classList.add('is-open');
        content.classList.add('is-open');
      }
    });
  }

  // --- COPY COUPON CODE ---
  function initCouponCopy() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-copy-coupon]');
      if (!btn) return;

      const code = btn.dataset.copyCoupon;
      navigator.clipboard.writeText(code).then(() => {
        showToast(`Coupon code "${code}" copied!`);
      });
    });
  }

  // --- NEWSLETTER POPUP ---
  function initNewsletterPopup() {
    const popup = document.getElementById('newsletter-popup');
    if (!popup) return;

    if (localStorage.getItem('softsignal_newsletter_dismissed')) return;

    setTimeout(() => {
      popup.classList.add('is-active');
    }, 6000);

    popup.querySelectorAll('[data-modal-close]').forEach((el) => {
      el.addEventListener('click', () => {
        popup.classList.remove('is-active');
        localStorage.setItem('softsignal_newsletter_dismissed', 'true');
      });
    });
  }

  // --- STICKY HEADER & MOBILE NAV ---
  function initHeader() {
    const header = document.getElementById('SiteHeader');
    if (!header) return;

    // Sticky scroll class
    window.addEventListener('scroll', () => {
      if (window.scrollY > 40) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }
    }, { passive: true });

    // Mobile nav elements
    const hamburger = document.getElementById('MobileMenuTrigger');
    const drawer = document.getElementById('MobileNavDrawer');
    const overlay = document.getElementById('MobileNavOverlay');
    const closeBtn = document.getElementById('MobileMenuClose');

    if (!hamburger || !drawer || !overlay) return;

    function openNav() {
      drawer.classList.add('is-open');
      overlay.classList.add('is-active');
      hamburger.classList.add('is-active');
      hamburger.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeNav() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-active');
      hamburger.classList.remove('is-active');
      hamburger.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    hamburger.addEventListener('click', () => {
      const isOpen = drawer.classList.contains('is-open');
      isOpen ? closeNav() : openNav();
    });

    overlay.addEventListener('click', closeNav);

    if (closeBtn) {
      closeBtn.addEventListener('click', closeNav);
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeNav();
        hamburger.focus();
      }
    });

    // Close when a nav link is tapped
    drawer.querySelectorAll('.mobile-nav-link').forEach((link) => {
      link.addEventListener('click', () => {
        closeNav();
      });
    });

    // Close nav on desktop resize
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 900 && drawer.classList.contains('is-open')) {
        closeNav();
      }
    }, { passive: true });
  }

  // --- INITIALIZE ALL ON DOM READY ---
  document.addEventListener('DOMContentLoaded', () => {
    CartDrawer.init();
    QuickView.init();
    SizeGuide.init();
    initProductVariants();
    initAccordions();
    initCouponCopy();
    initNewsletterPopup();
    initHeader();
  });
})();
